// ** Modules
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

export type AnchorNavigationItem = {
	label: string;
	id: string;
};

export type AnchorNavigationProps = {
	items: AnchorNavigationItem[];
	activeId?: string;
	showBorder?: boolean;
	onItemClick?: (id: string) => void;
	/** Landmark label for the nav (e.g. screen reader “Seitennavigation”). */
	navigationAriaLabel?: string;
};

/** Ref handle for wiring scroll-spy, `scrollIntoView`, IntersectionObserver, etc. */
export type AnchorNavigationRef = {
	/** Root `<nav>` landmark (sticky bar, observers, layout). */
	root: HTMLElement | null;
	/** Horizontal overflow container; use to scroll the active item into view. */
	scrollContainer: HTMLUListElement | null;
};

const AnchorNavigation = forwardRef<AnchorNavigationRef, AnchorNavigationProps>(function AnchorNavigation(
	{ items, activeId, showBorder = true, onItemClick, navigationAriaLabel = 'Seitennavigation' },
	ref
) {
	const theme = useTheme();
	const rootRef = useRef<HTMLElement>(null);
	const scrollContainerRef = useRef<HTMLUListElement>(null);
	const [scrollProgress, setScrollProgress] = useState(0);
	const [canScroll, setCanScroll] = useState(false);

	useImperativeHandle(
		ref,
		() => ({
			get root() {
				return rootRef.current;
			},
			get scrollContainer() {
				return scrollContainerRef.current;
			}
		}),
		[]
	);

	const updateScrollState = useCallback(() => {
		const el = scrollContainerRef.current;
		if (!el) return;

		const maxScroll = el.scrollWidth - el.clientWidth;
		setCanScroll(maxScroll > 0);

		if (maxScroll > 0) {
			setScrollProgress(el.scrollLeft / maxScroll);
		} else {
			setScrollProgress(0);
		}
	}, []);

	useEffect(() => {
		const el = scrollContainerRef.current;
		if (!el) return;

		updateScrollState();

		el.addEventListener('scroll', updateScrollState, { passive: true });
		window.addEventListener('resize', updateScrollState);

		return () => {
			el.removeEventListener('scroll', updateScrollState);
			window.removeEventListener('resize', updateScrollState);
		};
	}, [updateScrollState]);

	const handleClick = (id: string) => {
		onItemClick?.(id);
	};

	return (
		<Box ref={rootRef} component="nav" aria-label={navigationAriaLabel} sx={{ width: '100%', position: 'relative' }}>
			<Box
				ref={scrollContainerRef}
				component="ul"
				sx={{
					display: 'flex',
					alignItems: 'center',
					minHeight: 70,
					gap: { xs: '2rem', md: '3rem' },
					overflowX: 'auto',
					scrollbarWidth: 'none',
					listStyle: 'none',
					m: 0,
					p: 0,
					'&::-webkit-scrollbar': { display: 'none' }
				}}
			>
				{items.map((item, index) => {
					const isActive = item.id === activeId;
					const isFirst = index === 0;
					const isLast = index === items.length - 1;

					const contentAlignedPadding = (side: 'pl' | 'pr') => ({
						[side]: {
							xs: theme.spacing(theme.layout.pagePx.xs),
							md: theme.spacing(theme.layout.pagePx.md),
							lg: `calc(max(0px, (100vw - ${theme.layout.contentMaxWidthPx}px) / 2) + ${theme.spacing(theme.layout.pagePx.lg)})`
						}
					});

					return (
						<Box
							key={item.id}
							component="li"
							sx={{
								flexShrink: 0,
								...(isFirst && contentAlignedPadding('pl')),
								...(isLast && contentAlignedPadding('pr'))
							}}
						>
							<Typography
								component="a"
								href={`#${item.id}`}
								variant="body-md"
								onClick={() => handleClick(item.id)}
								aria-current={isActive ? 'true' : undefined}
								sx={{
									display: 'inline-block',
									cursor: 'pointer',
									whiteSpace: 'nowrap',
									pb: 0.5,
									textDecoration: 'none',
									color: isActive ? 'text.primary' : '#706F6F',
									fontWeight: isActive ? 500 : 400,
									borderBottom: isActive ? `2px solid ${theme.palette.text.primary}` : '2px solid transparent',
									transition: 'border-color 0.2s ease',
									'&:hover': {
										borderColor: theme.palette.text.secondary
									},
									'&:focusVisible': {
										outline: `2px solid ${theme.palette.primary.main}`,
										outlineOffset: 4,
										borderRadius: 1
									}
								}}
							>
								{item.label}
							</Typography>
						</Box>
					);
				})}
			</Box>

			{/* Bottom border */}
			{showBorder && (
				<Box
					aria-hidden
					sx={{
						height: '1px',
						bgcolor: theme.palette.grey[300]
					}}
				/>
			)}

			{/* Mobile scroll progress indicator (visual only; list scroll position) */}
			{canScroll && (
				<Box
					aria-hidden
					sx={{
						position: 'absolute',
						bottom: 0,
						left: 0,
						width: '100%',
						height: 3
					}}
				>
					<Box
						sx={{
							height: '100%',
							width: '30%',
							bgcolor: theme.palette.primary.main,
							borderRadius: 1,
							transform: `translateX(${scrollProgress * (100 / 0.3 - 100)}%)`,
							transition: 'transform 0.1s ease-out'
						}}
					/>
				</Box>
			)}
		</Box>
	);
});

AnchorNavigation.displayName = 'AnchorNavigation';

export default AnchorNavigation;
